import type {
  CleaningJob,
  CleaningTeam,
  PropertyCalendar,
  PropertyCalendarDeposit,
  PropertyCalendarReview,
  PropertyCalendarSupply,
} from '@/api/client'
import { isCleaningDrawn } from '@/components/calendar/cleaning-bars'
import { snapshotCaveats } from '@/lib/gap-events'
import { hasSupplyEventOn } from '@/lib/supply-events'
import type { StayDeposit } from './deposit-timeline'
import type { GapLookup } from './gap-sheet'
import type { LayerSummary } from './layers'
import type { StayReview } from './review-section'
import type { TurnoverCleaning } from './stay-sheet'
import type { SupplyLookup } from './supply-sheet'

/**
 * What a sheet is told about the thing it opens, from the calendar read behind
 * the page: the property tab's month read, or the portfolio calendar's windows
 * merged for one property. Pure, so both pages answer the same question the
 * same way.
 */

/**
 * A stay's deposit from the calendar read. The read is the whole month's, and
 * a deposit's events are listed under the stay that owns them, so a stay opened
 * from a pasted link that is outside this month simply has no item: that is
 * "nothing to show", not "no deposit".
 */
export function depositFor(
  reservationId: string | null,
  layer: LayerSummary,
  items: PropertyCalendarDeposit[]
): StayDeposit {
  if (layer.status === 'error') return layer
  if (layer.status === 'pending' || !reservationId) return { status: 'pending' }
  return {
    status: 'ok',
    deposit: items.find(item => item.reservation_id === reservationId) ?? null,
  }
}

/**
 * A stay's review window from the same calendar read. The layer lists a stay
 * for any month it touches, so a stay opened from a pasted link that is outside
 * this month simply has no item: "nothing to show", not "no review".
 */
export function reviewFor(
  reservationId: string | null,
  layer: LayerSummary,
  items: PropertyCalendarReview[],
  today: string | null
): StayReview {
  if (layer.status === 'error') return layer
  if (layer.status === 'pending' || !reservationId) return { status: 'pending' }
  return {
    status: 'ok',
    review: items.find(item => item.reservation_id === reservationId) ?? null,
    today,
  }
}

/**
 * The cleaning after a stay, as the same calendar read reported it: the job
 * whose source is that reservation. A stay can have more than one job over
 * time (a cancelled one, then its replacement), so a live one wins.
 */
export function turnoverCleaningFor(
  reservationId: string | null,
  layer: LayerSummary,
  jobs: CleaningJob[],
  teams: CleaningTeam[],
  crewLoading: boolean
): TurnoverCleaning {
  if (layer.status === 'error') return layer
  if (layer.status === 'pending' || !reservationId) return { status: 'pending' }
  const forStay = jobs.filter(
    job => job.source.kind === 'reservation' && job.source.id === reservationId
  )
  const job = forStay.find(isCleaningDrawn) ?? forStay[0] ?? null
  const team = job?.team_id ? teams.find(candidate => candidate.id === job.team_id) : undefined
  // The crew list is still on its way: the id the job carries is not its name.
  if (crewLoading && job?.team_id && !team)
    return { status: 'ok', job, crewName: null, crewLoading }
  return { status: 'ok', job, crewName: team?.name ?? job?.team_id ?? null }
}

/**
 * The opened category as the same calendar read reported it. A category is
 * listed when it has a runout or a delivery in the month, so a link to one
 * outside this month finds nothing: that is "not in the month shown", said as
 * such. While the month on screen is still the last one's data (paging), the
 * answer is "not read yet" and never "nothing there".
 */
export function supplyFor(
  target: { category: string; day: string },
  layer: LayerSummary,
  items: PropertyCalendarSupply[],
  isStale: boolean
): SupplyLookup {
  if (layer.status === 'error') return layer
  if (layer.status === 'pending' || isStale) return { status: 'pending' }
  // The day has to match too: a link for a runout that has since moved must
  // say so, not show the category's other event as if it were the one asked for.
  const supply = items.find(
    item => item.category === target.category && hasSupplyEventOn(item, target.day)
  )
  return { status: 'ok', supply: supply ?? null }
}

/**
 * The opened gap as the same calendar read reported it. The layer's error is
 * said as such - a property never scanned has no gaps listed, and that is not
 * "no gap" - and while the month on screen is still the last one's data
 * (paging) the answer is "not read yet", never "nothing there". A gap is
 * matched by its first night: gaps on one property never overlap.
 */
export function gapFor(
  firstNight: string,
  calendar: PropertyCalendar | undefined,
  isStale: boolean
): GapLookup {
  if (!calendar || isStale) return { status: 'pending' }
  const layer = calendar.layers.orphan_gaps
  if (layer.status === 'error') {
    return { status: 'error', message: layer.message, asOf: layer.as_of }
  }
  return {
    status: 'ok',
    gap: layer.items.find(gap => gap.first_night === firstNight) ?? null,
    asOf: layer.as_of,
    caveats: snapshotCaveats(layer, calendar.time_zone),
  }
}
